import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-media-query';
import { useTranslation } from 'react-i18next';

const STATUS_OPTIONS = [
  { value: 'active', labelKey: 'common.status.active' },
  { value: 'permitted', labelKey: 'common.status.permitted' },
  { value: 'expired', labelKey: 'common.status.expired' },
  { value: 'revoked', labelKey: 'common.status.revoked' },
  { value: 'pending', labelKey: 'common.status.pending' },
] as const;

const TYPE_OPTIONS = [
  { value: 'perpetual', labelKey: 'common.type.perpetual' },
  { value: 'subscription', labelKey: 'common.type.subscription' },
  { value: 'trial', labelKey: 'common.type.trial' },
] as const;

function useFilterParams(key: string): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = searchParams.get(key) ?? '';

  const setValue = useCallback(
    (next: string) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next) {
          p.set(key, next);
        } else {
          p.delete(key);
        }
        p.set('page', '1');
        return p;
      });
    },
    [key, setSearchParams]
  );

  return [value, setValue];
}

function FilterPopover({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: readonly { value: string; labelKey: string }[];
  selected: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation();
  const toggle = (value: string) => {
    if (selected === value) {
      onChange('');
    } else {
      onChange(value);
    }
  };

  const activeLabel = options.find((o) => o.value === selected)?.labelKey;
  const display = activeLabel ? `${label}: ${t(activeLabel)}` : label;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 border-input bg-transparent text-[13.5px] font-medium text-muted-foreground hover:text-foreground"
        >
          {display}
          <ChevronDown className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="space-y-1">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
            >
              <Checkbox
                checked={selected === opt.value}
                onCheckedChange={() => toggle(opt.value)}
              />
              <span className="text-sm">{t(opt.labelKey)}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function LicenseFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParams.get('search') ?? '');
  const [statusFilters, setStatusFilters] = useFilterParams('status');
  const [typeFilters, setTypeFilters] = useFilterParams('type');
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (searchValue.trim()) {
          p.set('search', searchValue.trim());
        } else {
          p.delete('search');
        }
        p.set('page', '1');
        return p;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, setSearchParams]);

  const activeFacetCount = (statusFilters ? 1 : 0) + (typeFilters ? 1 : 0);
  const hasFilters =
    searchValue.trim().length > 0 || statusFilters.length > 0 || typeFilters.length > 0;

  const clearFilters = () => {
    setSearchValue('');
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('search');
      p.delete('status');
      p.delete('type');
      p.set('page', '1');
      return p;
    });
  };

  const popovers = (
    <>
      <FilterPopover
        label={t('licenses.filters.status')}
        options={STATUS_OPTIONS}
        selected={statusFilters}
        onChange={setStatusFilters}
      />
      <FilterPopover
        label={t('licenses.filters.type')}
        options={TYPE_OPTIONS}
        selected={typeFilters}
        onChange={setTypeFilters}
      />
    </>
  );

  return (
    <div className="sticky top-0 z-10 border-b bg-background pb-3 pt-1">
      <div className="flex items-center gap-2.5">
        <div className="relative min-w-0 flex-1">
          <Search
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
            aria-hidden="true"
          />
          <Input
            placeholder={t('licenses.filters.searchPlaceholder')}
            className="h-9 border-input bg-transparent pl-9 text-[13.5px]"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            aria-label={t('licenses.filters.searchAria')}
          />
        </div>
        {isMobile ? (
          <Button
            variant="outline"
            size="sm"
            className="h-9 shrink-0 gap-1.5 border-input bg-transparent text-[13.5px] font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
            {t('common.filtersToggle')}
            {activeFacetCount > 0 && (
              <span className="inline-block min-w-[18px] rounded-full bg-primary/15 px-1.5 py-px text-center text-[11px] font-semibold text-primary">
                {activeFacetCount}
              </span>
            )}
          </Button>
        ) : (
          popovers
        )}
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-9 shrink-0 gap-1 text-[13px] text-muted-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            {t('common.clearFilters')}
          </Button>
        )}
      </div>
      {isMobile && expanded && <div className="mt-2.5 flex flex-wrap gap-2">{popovers}</div>}
    </div>
  );
}
